/**
 * @fileoverview Server-side helper to load frontend configuration from config files.
 *
 * This function loads frontend config for use in getServerSideProps,
 * matching the shape consumed by FrontendConfigProvider.
 */

import { GetServerSidePropsContext, NextPageContext } from 'next';
import { FrontendConfigContextType } from '../context/frontendConfig';
import {
  mergeFrontendConfig,
  parseFrontendOverridesFromReq,
} from './frontendConfigOverrides';

let frontendConfigCache: FrontendConfigContextType | null = null;

type GetUniversalFrontendConfigParams = {
  ctx?: GetServerSidePropsContext | NextPageContext;
  host?: string;
};

/**
 * Gets the value of an environment variable at runtime.
 *
 * @param key
 *  The env key to get.
 * @returns
 *  The value of the env key or undefined if not found.
 */
const getRuntimeEnv = (key: string): string | undefined => {
  if (typeof process === 'undefined') {
    return undefined;
  }

  return process.env[key];
};

/**
 * Gets frontend configuration for server-side use (getServerSideProps).
 *
 * @param ctx
 *   The Next.js context object (optional, defaults to using env var).
 *
 * @returns
 *   Frontend configuration object consumed by FrontendConfigProvider.
 */
export const getServerFrontendConfig = (
  ctx?: GetServerSidePropsContext | NextPageContext,
): FrontendConfigContextType => {
  // Get frontend key from env var (same as next.config.dist.js).
  const frontendKey = process.env.NEXT_PUBLIC_FRONTEND || 'default';

  // Use dynamic require to load config files.
  // Note: This uses the same getFrontendConfig logic but in TypeScript.
  const { getFrontendConfig } = require('../utils/getConfig');

  // Load all config files.
  const appConfig = getFrontendConfig('app', frontendKey as string);
  const jobsConfig = getFrontendConfig('jobs', frontendKey as string);
  const resumeConfig = getFrontendConfig('resume', frontendKey as string);
  const organizationsConfig = getFrontendConfig(
    'organizations',
    frontendKey as string,
  );
  const userConfig = getFrontendConfig('user', frontendKey as string);
  const wizardConfig = getFrontendConfig('wizard', frontendKey as string);

  // Build config consumed by FrontendConfigProvider.
  const baseConfig: FrontendConfigContextType = {
    APP: appConfig.public || {},
    JOBS: jobsConfig.public || {},
    RESUME: resumeConfig.public || {},
    ORGANIZATIONS: organizationsConfig.public || {},
    USER: userConfig.public || {},
    WIZARD: wizardConfig.public || {},
    API_KEYS: {
      GOOGLE_AUTOCOMPLETE_KEY: getRuntimeEnv('GOOGLE_AUTOCOMPLETE_KEY'),
      STRIPE_API_KEY: getRuntimeEnv('STRIPE_API_KEY'),
    },
    frontend: (frontendKey as string) || 'default',
  };

  // Apply test-only runtime overrides from cookies (SSR-safe).
  const overrides = parseFrontendOverridesFromReq(ctx?.req);
  return mergeFrontendConfig(baseConfig, overrides);
};

/**
 * Gets frontend configuration for server or client with caching on the client.
 *
 * Should be called very sparingly, specifically from the
 * _app.tsx getInitialProps to load config for the entire app.
 */
export const getUniversalFrontendConfig = async (
  params: GetUniversalFrontendConfigParams,
): Promise<FrontendConfigContextType> => {
  const { ctx } = params;

  if (ctx?.req) {
    // Running on the server, load config directly from files.
    return getServerFrontendConfig(ctx);
  }

  if (frontendConfigCache) {
    return frontendConfigCache;
  }

  try {
    // Running on the client, fetch config from API route.
    // Cache it to avoid unnecessary API calls on subsequent page transitions.
    const response = await fetch(`/api/frontend-config`);
    frontendConfigCache = await response.json();
  } catch (e) {
    frontendConfigCache = getServerFrontendConfig();
  }

  return frontendConfigCache as FrontendConfigContextType;
};
